import { Grid } from '@mui/material';
import Input from '../../../components/Input/Input';
import { INTEGERS_UNIT } from '../../../constants/regex';
import { useTranslation } from 'react-i18next';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { TEST_IDS } from '../consts/testIds';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { EstadoExpuestoEnum, getEstadoExpuestoOptions } from '../consts/periodicosTypes';
import {
    useGetEstablecimientosPeriodicosQuery,
    useGetLocalidadesPrestadoresQuery,
    useGetPrestadoresQuery,
    useGetRiesgosQuery
} from '../../../services/periodicosApi';
import { useRoles } from '../../../hooks/useRoles';
import { useEffect, useMemo } from 'react';

interface ExposedFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    tabIndex: number;
    reconfirmation?: boolean;
    handleSubmitSearch?: () => void;
    setFilters?: (fn: any) => void;
    selectedEstablishmentId?: any;
    location?: any;
}

const PeriodicosFilters = (props: ExposedFiltersProps) => {
    const {
        extraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters,
        tabIndex,
        reconfirmation = false,
        setFilters,
        selectedEstablishmentId,
        location
    } = props;
    const { data: prestadoresOptions, isLoading: isLoadingPrestadores } = useGetPrestadoresQuery(
        {}
    );

    const { t } = useTranslation('periodicos');
    const userData = useAppSelector((state) => state.auth.user);
    const { isAuditor } = useRoles();
    const { data: establishmentsOptions, isFetching: isFetchingEstablishmentsOptions } =
        useGetEstablecimientosPeriodicosQuery();

    const { data: riesgosOptions, isLoading: isLoadingRiesgos } = useGetRiesgosQuery();

    const { data: localidadesOptions, isLoading: isLoadingLocalidades } =
        useGetLocalidadesPrestadoresQuery({});

    const { contracts } = useContracts();
    const contractOptions = contracts.map((contract) => ({
        nombre: `${contract.nombre}`,
        id: contract.id,
        value: contract.nroContrato
    }));

    const establishmentOptions = establishmentsOptions
        ? establishmentsOptions
              .filter((e) => {
                  if (reconfirmation && extraFilters?.idContrato?.id) {
                      return (
                          String(
                              e.organismoCentralId ?? e.organismo?.organismoCentral?.id ?? ''
                          ) === String(extraFilters.idContrato.id)
                      );
                  }

                  if (!reconfirmation && extraFilters?.idOrganismoCentral) {
                      return (
                          e.organismo?.organismoCentral?.id === extraFilters.idOrganismoCentral.id
                      );
                  }

                  return true;
              })
              .map((e) => ({
                  id: e.id,
                  nombre: `${e.nombre} - ${t('header.extraFilters.codSRT')}: ${
                      e.numero ?? e.codigoSRT ?? ''
                  }`,
                  organismoCentral: e.organismo?.organismoCentral?.id,
                  contratoId: e.organismoCentralId ?? e.organismo?.organismoCentral?.id
              }))
        : [];

    const localidadOptionsFormatted =
        localidadesOptions?.map((l) => ({
            id: l.id,
            nombre: `${l.codigoPostal} - ${l.nombre}`
        })) || [];

    const riesgoOptionsFormatted =
        riesgosOptions?.map((riesgo) => ({
            id: riesgo.id,
            nombre: `${riesgo.codigo} - ${riesgo.descripcion}`
        })) || [];

    const prestadoresOptionsFormatted =
        prestadoresOptions?.map((p) => ({
            id: p.id,
            nombre: p.razonSocial
        })) || [];
    const fieldSize = tabIndex === 0 || tabIndex === 1 ? 4 : 3;

    const matchingEstablishment = useMemo(
        () =>
            !selectedEstablishmentId
                ? null
                : establishmentsOptions?.find((e) => e.id === selectedEstablishmentId),
        [selectedEstablishmentId, establishmentsOptions, location]
    );


   useEffect(() => {
       if (matchingEstablishment?.id) {
           handleChangeExtraFilters({
               target: { name: 'idEstablecimiento', value: matchingEstablishment }
           });
       }
   }, [matchingEstablishment?.id, establishmentOptions, location]);

    useEffect(() => {
        if (matchingEstablishment) {
            if (handleSubmitSearch) {
                handleSubmitSearch();
            }
        }
    }, [extraFilters?.idEstablecimiento, location]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={fieldSize}>
                    <Input
                        label={`${t('header.extraFilters.cuil')}`}
                        size="small"
                        name="cuil"
                        onChange={(e: any) => {
                            const value = e.target.value;
                            if (
                                value === null ||
                                value === '' ||
                                (INTEGERS_UNIT.test(value) && value?.length <= 11)
                            ) {
                                handleChangeExtraFilters(e);
                            }
                        }}
                        value={extraFilters?.cuil || ''}
                        testID={TEST_IDS.extraFilters.cuil}
                        clearable
                    />
                </Grid>

                <Grid item xs={fieldSize}>
                    <Input
                        label={`${t('header.extraFilters.name')}`}
                        size="small"
                        name="nombre"
                        onChange={handleChangeExtraFilters}
                        value={extraFilters?.nombre || ''}
                        testID={TEST_IDS.extraFilters.name}
                        clearable
                    />
                </Grid>
                {tabIndex !== 5 && !isAuditor && (
                    <Grid item xs={fieldSize}>
                        <AutocompleteComponent
                            label={t('header.extraFilters.establishment')}
                            size="small"
                            name="idEstablecimiento"
                            disabled={isFetchingEstablishmentsOptions || matchingEstablishment}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idEstablecimiento', value: value || '' }
                                });
                            }}
                            value={matchingEstablishment || extraFilters?.idEstablecimiento || null}
                            options={establishmentOptions}
                            clearable
                        />
                    </Grid>
                )}
                {tabIndex !== 5 && (
                    <Grid item xs={fieldSize}>
                        <AutocompleteComponent
                            label={t('header.extraFilters.localidad')}
                            size="small"
                            name="idLocalidad"
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idLocalidad', value: value || '' }
                                });
                            }}
                            value={extraFilters?.idLocalidad || null}
                            options={localidadOptionsFormatted}
                            loading={isLoadingLocalidades}
                            clearable
                        />
                    </Grid>
                )}
                <Grid item xs={fieldSize}>
                    <AutocompleteComponent
                        label={`${t('header.extraFilters.contract')}`}
                        size="small"
                        name={reconfirmation ? 'idContrato' : 'idOrganismoCentral'}
                        onChange={(value: any) => {
                            handleChangeExtraFilters({
                                target: {
                                    name: reconfirmation ? 'idContrato' : 'idOrganismoCentral',
                                    value: value || ''
                                }
                            });

                            if (setFilters) {
                                setFilters((prev: any) => ({
                                    ...prev,
                                    idOrganismoCentral: value?.id ?? ''
                                }));
                            }
                        }}
                        value={
                            reconfirmation
                                ? extraFilters?.idContrato
                                : extraFilters?.idOrganismoCentral || null
                        }
                        options={contractOptions}
                        testID={TEST_IDS.extraFilters.contract}
                        clearable
                    />
                </Grid>

                <Grid item xs={fieldSize}>
                    <AutocompleteComponent
                        label={t('header.extraFilters.risk')}
                        size="small"
                        name="idRiesgo"
                        onChange={(value: any) => {
                            handleChangeExtraFilters({
                                target: { name: 'idRiesgo', value: value || '' }
                            });
                        }}
                        value={extraFilters?.idRiesgo || null}
                        options={riesgoOptionsFormatted}
                        loading={isLoadingRiesgos}
                        clearable
                    />
                </Grid>

                {tabIndex >= 2 && tabIndex !== 5 && (
                    <Grid item xs={fieldSize}>
                        <AutocompleteComponent
                            label={t('header.extraFilters.prestador')}
                            size="small"
                            name="idPrestador"
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idPrestador', value: value?.id ?? '' }
                                });
                            }}
                            value={
                                prestadoresOptionsFormatted.find(
                                    (p) => p.id === extraFilters?.idPrestador
                                ) || null
                            }
                            options={prestadoresOptionsFormatted}
                            loading={isLoadingPrestadores}
                            clearable
                        />
                    </Grid>
                )}

                {tabIndex >= 3 && tabIndex !== 5 && (
                    <Grid item xs={fieldSize}>
                        <AutocompleteComponent
                            label={t('header.extraFilters.state')}
                            size="small"
                            name="estadoGeneral"
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'estadoGeneral', value: value?.id ?? '' }
                                });
                            }}
                            value={
                                getEstadoExpuestoOptions(t)
                                    .filter((estado) =>
                                        [
                                            EstadoExpuestoEnum.CONVOCADO,
                                            EstadoExpuestoEnum.TURNO_ASIGNADO,
                                            EstadoExpuestoEnum.CON_RESULTADO
                                        ].includes(estado.id)
                                    )
                                    .find((estado) => estado.id === extraFilters?.estadoGeneral) ||
                                null
                            }
                            options={getEstadoExpuestoOptions(t).filter((estado) =>
                                [
                                    EstadoExpuestoEnum.CONVOCADO,
                                    EstadoExpuestoEnum.TURNO_ASIGNADO,
                                    EstadoExpuestoEnum.CON_RESULTADO
                                ].includes(estado.id)
                            )}
                            clearable
                        />
                    </Grid>
                )}

                {tabIndex === 5 && (
                    <Grid item xs={fieldSize}>
                        <AutocompleteComponent
                            label={t('header.extraFilters.reconfirmado')}
                            size="small"
                            name="reconfirmacion"
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'reconfirmacion', value: value?.value || '' }
                                });
                            }}
                            value={
                                extraFilters?.reconfirmacion
                                    ? {
                                          nombre:
                                              extraFilters.reconfirmacion === 'si'
                                                  ? t('general.yes')
                                                  : t('general.no'),
                                          value: extraFilters.reconfirmacion
                                      }
                                    : null
                            }
                            options={[
                                { nombre: t('general.yes'), value: 'si' },
                                { nombre: t('general.no'), value: 'no' }
                            ]}
                            clearable
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export { PeriodicosFilters };
